/**
 * Hand-written types for the rule AST.
 *
 * The generated client (`ringil-api.v2.tsx`) types `RuleAst.condition` as `any` and emits
 * `AndNode` / `OrNode` / `NotNode` / `ComparisonNode` as *empty* interfaces: the OpenAPI generator
 * drops Jackson's polymorphic subtypes. The wire shape is nevertheless fixed — it is
 * `engine/RuleAst.kt` in frontend-service, a sealed interface discriminated on `op` — so we
 * re-declare it here as a proper union and cast at the API boundary. Nothing else in the feature
 * touches the generated AST types.
 */

/** Discriminator values, from `@JsonSubTypes` on the backend's `ConditionNode`. */
export const CONDITION_OPS = ['AND', 'OR', 'NOT', 'COMPARISON'] as const;
export type ConditionOp = (typeof CONDITION_OPS)[number];

export type OperatorKey =
  | 'EQ'
  | 'NEQ'
  | 'GT'
  | 'GTE'
  | 'LT'
  | 'LTE'
  | 'IN'
  | 'NOT_IN'
  | 'CONTAINS'
  | 'STARTS_WITH'
  | 'IS_NULL'
  | 'IS_NOT_NULL'
  | 'BETWEEN'
  | 'IN_GROUP'
  | 'NOT_IN_GROUP';

/**
 * A value carrying a unit is stored as an object, not a scalar — see `dsl/Literals.kt`. The unit
 * is kept as written (`12 t` stays `12 t`, it is not normalised to `12000`), which is what makes
 * the text -> AST -> text round trip exact. Normalisation happens where the comparison is made.
 */
export interface QuantityValue {
  value: number;
  unit: string;
}

export interface MoneyValue {
  value: number;
  currency: string;
}

/**
 * `now`, `now + 2 hours`, `now - 3 days` — a time relative to when the rule is evaluated, for
 * DATETIME facts. `value` is signed; absent value and unit mean plain `now`. The unit is a duration
 * keyword as written, singular or plural.
 */
export interface RelativeTimeValue {
  relativeTo: 'now';
  value?: number;
  unit?: string;
}

/**
 * A record — `band(from: 0, to: 10, price: 3000 CZK)` — stored as an object with its name in
 * `kind` and its fields in the order written. Today only the parts of a `price_tariff` are records.
 */
export interface RecordValue {
  kind: string;
  [field: string]: RuleValue;
}

export type RuleScalar = string | number | boolean | null;
export type RuleValue = RuleScalar | QuantityValue | MoneyValue | RelativeTimeValue | RecordValue | RuleValue[];

export interface AndNode {
  op: 'AND';
  children: ConditionNode[];
}

export interface OrNode {
  op: 'OR';
  children: ConditionNode[];
}

export interface NotNode {
  op: 'NOT';
  child: ConditionNode;
}

export interface ComparisonNode {
  op: 'COMPARISON';
  fact: string;
  operator: OperatorKey;
  /**
   * Absent for IS_NULL / IS_NOT_NULL, a two-element list for BETWEEN, a list for IN / NOT_IN. A
   * single group code for IN_GROUP / NOT_IN_GROUP — the group, not its members.
   */
  value?: RuleValue;
  /**
   * Arguments of a parameterised fact — `expedition.shipmentDuration(first: max, last: min)`.
   * Absent when the fact takes none or none were written; the backend then applies the defaults.
   */
  args?: Record<string, string>;
}

export type ConditionNode = AndNode | OrNode | NotNode | ComparisonNode;

export type GroupNode = AndNode | OrNode;

export interface TriggerSpec {
  type: string;
  /** `on expedition.created after 5 minutes`. Delays the event, not the trigger. */
  delayMinutes?: number;
}

export interface ActionCall {
  action: string;
  params: Record<string, RuleValue>;
}

/** A rule with no condition fires on every occurrence of its trigger. */
export interface TypedRuleAst {
  trigger: TriggerSpec;
  condition?: ConditionNode;
  actions: ActionCall[];
}

/** The rule header lives in the source text, so `format` needs it alongside the AST. */
export interface RuleHeader {
  name: string;
  enabled: boolean;
  priority: number;
}

// ---------------------------------------------------------------------------
// Guards
// ---------------------------------------------------------------------------

export const isGroup = (node: ConditionNode): node is GroupNode => node?.op === 'AND' || node?.op === 'OR';
export const isNot = (node: ConditionNode): node is NotNode => node?.op === 'NOT';
export const isComparison = (node: ConditionNode): node is ComparisonNode => node?.op === 'COMPARISON';

export const isRelativeTimeValue = (value: RuleValue): value is RelativeTimeValue =>
  typeof value === 'object' && value !== null && !Array.isArray(value) && (value as RelativeTimeValue).relativeTo === 'now';

// A relative time carries a `unit` too, so it has to be ruled out here — or `now + 2 hours` would
// read as the quantity `2 hours` everywhere a value is inspected.
export const isQuantityValue = (value: RuleValue): value is QuantityValue =>
  typeof value === 'object' && value !== null && !Array.isArray(value) && 'unit' in value && !isRelativeTimeValue(value);

export const isMoneyValue = (value: RuleValue): value is MoneyValue =>
  typeof value === 'object' && value !== null && !Array.isArray(value) && 'currency' in value;

export const isRecordValue = (value: RuleValue): value is RecordValue =>
  typeof value === 'object' && value !== null && !Array.isArray(value) && typeof (value as RecordValue).kind === 'string';

/** The bare amount, whether the value is a plain number or carries a unit. */
export const amountOf = (value: RuleValue): number | undefined => {
  if (typeof value === 'number') return value;
  if (isQuantityValue(value) || isMoneyValue(value)) return value.value;
  return undefined;
};

/** The unit a value carries, or undefined for a plain one. Currencies count as units here. */
export const unitOf = (value: RuleValue): string | undefined => {
  if (isQuantityValue(value)) return value.unit;
  if (isMoneyValue(value)) return value.currency;
  return undefined;
};

/** Rebuilds a value with the same unit (if any) around a new amount. */
export const withAmount = (previous: RuleValue, amount: number): RuleValue => {
  if (isMoneyValue(previous)) return { value: amount, currency: previous.currency };
  if (isQuantityValue(previous)) return { value: amount, unit: previous.unit };
  return amount;
};

/** Rebuilds a value with the same amount around a new unit. An empty unit drops back to a scalar. */
export const withUnit = (previous: RuleValue, unit: string): RuleValue => {
  const amount = amountOf(previous) ?? 0;
  return unit ? { value: amount, unit } : amount;
};

/**
 * The shape of value an operator wants. List operators need a list, BETWEEN needs a pair, the rest
 * need a scalar — including IN_GROUP / NOT_IN_GROUP, whose value is one group code. Switching
 * between operators of different shapes has to drop the value, so `in [CZE]` -> `in group` resets.
 */
export const valueShapeOf = (operator: OperatorKey): 'none' | 'list' | 'range' | 'scalar' => {
  if (operator === 'IS_NULL' || operator === 'IS_NOT_NULL') return 'none';
  if (operator === 'IN' || operator === 'NOT_IN') return 'list';
  if (operator === 'BETWEEN') return 'range';
  return 'scalar';
};

/**
 * Whether a comparison's value still makes sense after its operator changes. The shape has to
 * match, and so does the list the value is picked from: `== CZE` and `in group EU` are both
 * scalars, but a country is not a group. `valueList` is the operator's `valueEntityRefType`.
 */
export const valueSurvivesOperatorChange = (
  previous: { key: OperatorKey; valueList?: string | null },
  next: { key: OperatorKey; valueList?: string | null },
): boolean => valueShapeOf(previous.key) === valueShapeOf(next.key) && (previous.valueList ?? null) === (next.valueList ?? null);

/** `now`, `now + 2 hours`, `now - 3 days` — the value as the rule text writes it. */
export const relativeTimeText = (value: RelativeTimeValue): string => {
  if (!value.value || !value.unit) return 'now';
  return `now ${value.value < 0 ? '-' : '+'} ${Math.abs(value.value)} ${value.unit}`;
};

/**
 * A value as the rule text writes it — `25 CZK`, `[EP, IBC]`, `band(from: 0, price: 3000 CZK)`.
 * For reading, not for saving: the backend's printer owns the source. Strings are written bare,
 * which is right for the codes and keywords records hold; free text is quoted.
 */
export const valueText = (value: RuleValue): string => {
  if (value === null || value === undefined) return 'null';
  if (Array.isArray(value)) return `[${value.map(valueText).join(', ')}]`;
  if (isRelativeTimeValue(value)) return relativeTimeText(value);
  if (isRecordValue(value)) {
    const fields = Object.entries(value).filter(([name, field]) => name !== 'kind' && field !== undefined);
    return `${value.kind}(${fields.map(([name, field]) => `${name}: ${valueText(field)}`).join(', ')})`;
  }
  if (isMoneyValue(value)) return value.currency ? `${value.value} ${value.currency}` : String(value.value);
  if (isQuantityValue(value)) return `${value.value} ${value.unit}`;
  if (typeof value === 'string') return /^[A-Za-z_][A-Za-z0-9_]*$/.test(value) ? value : JSON.stringify(value);
  return String(value);
};

/** The duration keywords in their plural form, which is what the builder's unit selector lists. */
export const pluralDurationUnit = (unit: string | undefined): string | undefined => (unit ? (unit.endsWith('s') ? unit : `${unit}s`) : undefined);

/**
 * A duration — `48 hours`, `1 day`, `-6 hours` — from the builder's amount and plural unit. One of
 * a unit is written singular, as an author would type it. Negative is legal: a span between two
 * overlapping windows is.
 */
export const durationValue = (amount: number | null | undefined, pluralUnit: string): QuantityValue => {
  const value = amount ?? 0;
  return { value, unit: Math.abs(value) === 1 ? pluralUnit.replace(/s$/, '') : pluralUnit };
};

/**
 * Builds a relative time from the builder's parts. Zero or no amount is plain `now` — an offset of
 * nothing is not an offset. One of a unit is written singular (`now + 1 day`), so the generated
 * text reads the way an author would type it.
 */
export const relativeTime = (sign: '+' | '-', amount: number | null | undefined, pluralUnit: string): RelativeTimeValue => {
  if (!amount) return { relativeTo: 'now' };
  const magnitude = Math.abs(amount);
  const unit = magnitude === 1 ? pluralUnit.replace(/s$/, '') : pluralUnit;
  return { relativeTo: 'now', value: sign === '-' ? -magnitude : magnitude, unit };
};

// ---------------------------------------------------------------------------
// Constructors
// ---------------------------------------------------------------------------

export const emptyComparison = (fact = '', operator: OperatorKey = 'EQ'): ComparisonNode => ({
  op: 'COMPARISON',
  fact,
  operator,
});

export const emptyGroup = (op: 'AND' | 'OR' = 'AND'): GroupNode => ({ op, children: [emptyComparison()] });

export const emptyAst = (triggerType = ''): TypedRuleAst => ({
  trigger: { type: triggerType },
  actions: [],
});

// ---------------------------------------------------------------------------
// Immutable tree operations, addressed by path
// ---------------------------------------------------------------------------

/**
 * Nodes carry no id — the wire format has none, and inventing one would have to be stripped on
 * every call. So the builder addresses nodes by their path from the root: the index of each child
 * taken on the way down (a NOT has exactly one child, at index 0). The root itself is `[]`.
 */
export type NodePath = number[];

const childrenOf = (node: ConditionNode): ConditionNode[] => {
  if (isGroup(node)) return node.children;
  if (isNot(node)) return [node.child];
  return [];
};

const withChildren = (node: ConditionNode, children: ConditionNode[]): ConditionNode => {
  if (isGroup(node)) return { ...node, children };
  if (isNot(node)) return { ...node, child: children[0] };
  return node;
};

export const getNodeAt = (root: ConditionNode, path: NodePath): ConditionNode | undefined =>
  path.reduce<ConditionNode | undefined>((node, index) => (node ? childrenOf(node)[index] : undefined), root);

/** Returns a new tree with the node at `path` replaced by `updater`'s result. */
export const updateNodeAt = (root: ConditionNode, path: NodePath, updater: (node: ConditionNode) => ConditionNode): ConditionNode => {
  if (path.length === 0) return updater(root);
  const [index, ...rest] = path;
  const children = childrenOf(root);
  const updated = children.map((child, i) => (i === index ? updateNodeAt(child, rest, updater) : child));
  return withChildren(root, updated);
};

/**
 * Removes the node at `path`. Removing the last child of a group removes the group too, so the
 * tree never keeps an empty AND/OR that would print as invalid source. Returns undefined when the
 * whole tree is gone — a rule with no condition, which is legal and means "always".
 */
export const removeNodeAt = (root: ConditionNode, path: NodePath): ConditionNode | undefined => {
  if (path.length === 0) return undefined;
  const [index, ...rest] = path;
  const children = childrenOf(root);

  if (rest.length === 0) {
    // A NOT without its child is meaningless, so it goes with it.
    if (isNot(root)) return undefined;
    const remaining = children.filter((_, i) => i !== index);
    if (remaining.length === 0) return undefined;
    if (remaining.length === 1) return remaining[0];
    return withChildren(root, remaining);
  }

  const updatedChild = removeNodeAt(children[index], rest);
  const remaining = updatedChild ? children.map((child, i) => (i === index ? updatedChild : child)) : children.filter((_, i) => i !== index);
  if (remaining.length === 0) return undefined;
  if (remaining.length === 1 && isGroup(root)) return remaining[0];
  return withChildren(root, remaining);
};

/** Appends a child to the group at `path`. */
export const appendChildAt = (root: ConditionNode, path: NodePath, node: ConditionNode): ConditionNode =>
  updateNodeAt(root, path, target => (isGroup(target) ? { ...target, children: [...target.children, node] } : target));

/** Wraps the node at `path` in a new group of the given kind, keeping it as the first child. */
export const wrapInGroupAt = (root: ConditionNode, path: NodePath, op: 'AND' | 'OR'): ConditionNode =>
  updateNodeAt(root, path, target => ({ op, children: [target, emptyComparison()] }));

/** Flips an AND group to OR or back, in place. */
export const toggleGroupOpAt = (root: ConditionNode, path: NodePath): ConditionNode =>
  updateNodeAt(root, path, target => (isGroup(target) ? { ...target, op: target.op === 'AND' ? 'OR' : 'AND' } : target));

/** Wraps the node at `path` in a NOT, or unwraps it when it already is one. */
export const toggleNotAt = (root: ConditionNode, path: NodePath): ConditionNode =>
  updateNodeAt(root, path, target => (isNot(target) ? target.child : { op: 'NOT', child: target }));
